// Worker page section seam, web half (E-3 L3 M3; IF-53, frozen at the M3 exit).
//
// Each apps/admin/src/web/pages/worker/sections/<key>.tsx exports one
// `section: WorkerSectionWeb`. Vite's import.meta.glob builds the list at build
// time, so there is no shared list: a consumer lane (L20f, P7-D03) adds a new
// file in its own slice and never edits an L3 file (CF-57).
import type { ComponentType } from 'react';

export interface WorkerSectionWeb {
  readonly key: string;
  readonly order: number;
  readonly label: string;
  /** The PERMS bit that shows the section (WORKER_READ for L3's seven). */
  readonly perm: bigint;
  readonly Component: ComponentType;
}

const SECTION_KEY_RE = /^[a-z][a-z0-9-]{0,63}$/;

/**
 * Validates the glob's modules (each must export a well-formed `section`),
 * rejects duplicate keys and sorts by order, then key.
 */
export function buildWorkerWebSections(
  modules: Readonly<Record<string, unknown>>,
): readonly WorkerSectionWeb[] {
  const byKey = new Map<string, string>();
  const out: WorkerSectionWeb[] = [];
  for (const [file, mod] of Object.entries(modules).sort(([a], [b]) => (a < b ? -1 : 1))) {
    const s = (mod as { section?: Partial<WorkerSectionWeb> } | null)?.section;
    if (
      !s ||
      typeof s.key !== 'string' ||
      !SECTION_KEY_RE.test(s.key) ||
      typeof s.order !== 'number' ||
      !Number.isFinite(s.order) ||
      typeof s.label !== 'string' ||
      typeof s.perm !== 'bigint' ||
      (typeof s.Component !== 'function' && typeof s.Component !== 'object')
    ) {
      throw new Error(`worker web section ${file}: must export a well-formed \`section\``);
    }
    const prior = byKey.get(s.key);
    if (prior !== undefined) {
      throw new Error(`worker web section key "${s.key}" is declared by both ${prior} and ${file}`);
    }
    byKey.set(s.key, file);
    out.push(s as WorkerSectionWeb);
  }
  return out.sort((a, b) => a.order - b.order || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}

export const WORKER_WEB_SECTIONS: readonly WorkerSectionWeb[] = buildWorkerWebSections(
  import.meta.glob(['./sections/*.tsx', '!./sections/*.test.tsx'], { eager: true }),
);
